import { endpoint, request } from "./api";
import type { Binding } from "./types";

function b64uToBuf(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - value.length % 4) % 4);
  return Uint8Array.from(atob(padded), character => character.charCodeAt(0)).buffer;
}

function bufToB64u(value: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(value));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function creationOptions(value: any): PublicKeyCredentialCreationOptions {
  const options = value.publicKey ? { ...value.publicKey } : { ...value };
  options.challenge = b64uToBuf(options.challenge);
  options.user = { ...options.user, id: b64uToBuf(options.user.id) };
  options.excludeCredentials = (options.excludeCredentials || []).map((item: any) => ({
    ...item,
    id: b64uToBuf(item.id),
  }));
  return options;
}

function attestation(credential: PublicKeyCredential): unknown {
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: bufToB64u(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufToB64u(response.clientDataJSON),
      attestationObject: bufToB64u(response.attestationObject),
      transports: response.getTransports?.() || [],
    },
    extensions: credential.getClientExtensionResults?.() || {},
  };
}

async function assertionFor(binding: Binding, challenge: string): Promise<unknown> {
  const credential = await navigator.credentials.get({ publicKey: {
    challenge: b64uToBuf(challenge),
    rpId: location.hostname,
    userVerification: binding.http.protocol.required as UserVerificationRequirement,
    allowCredentials: [],
    timeout: 60000,
  }}) as PublicKeyCredential | null;
  if (!credential) throw new Error(binding.prose.failure);
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    auth_data: bufToB64u(response.authenticatorData),
    client_data_json: bufToB64u(response.clientDataJSON),
    sig: bufToB64u(response.signature),
  };
}

export async function registerPasskey(binding: Binding): Promise<void> {
  const start = await request<any>(binding, binding.http.api.passkey_register_start, {});
  const credential = await navigator.credentials.create({ publicKey: creationOptions(start.public_key) }) as PublicKeyCredential | null;
  if (!credential) throw new Error(binding.prose.failure);
  await request(binding, binding.http.api.passkey_register_finish, { credential: attestation(credential) });
}

export async function occupySeat(binding: Binding, seat: string): Promise<void> {
  const prefix = endpoint(binding.http.api.seat_prefix, seat, binding.http.api.seat_occupy_start_suffix);
  const start = await request<any>(binding, prefix, {});
  const finish = endpoint(binding.http.api.seat_prefix, seat, binding.http.api.seat_occupy_finish_suffix);
  await request(binding, finish, {
    credential: start.credential,
    assertion: await assertionFor(binding, start.challenge),
  });
}

export async function pressProd(binding: Binding, requestId: string): Promise<void> {
  const startPath = endpoint(binding.http.api.promotion_prefix, requestId, binding.http.api.press_prod_start_suffix);
  const start = await request<any>(binding, startPath, {});
  const finishPath = endpoint(binding.http.api.promotion_prefix, requestId, binding.http.api.press_prod_finish_suffix);
  await request(binding, finishPath, {
    intent: start.intent,
    assertion: await assertionFor(binding, start.challenge),
  });
}
